import { faTimes, faCheckCircle, faTimesCircle, faArrowsRotate } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal, ButtonToolbar, Button as BButton } from 'react-bootstrap'
import { Row } from 'antd'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { testAuth } from 'api/integrations/coursera/testAuth'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import Button from 'design-system/button'
import {
  ButtonVariant,
  ButtonSizes,
  ButtonType,
} from 'design-system/button/src/Button'
import { IntegrationCoursera, IntegrationTypes } from 'models/integrations'
import { ErrorToast, SuccessToast } from 'components/ui/toast/toastsTemplates'
import { upsertIntegration } from 'api/integrations'
import styles from './index.module.scss'

interface FormData {
  orgId: string
  appKey: string
  appSecret: string
}

interface modalCourseraProps {
  show: boolean
  onClose(): void
  integration?: IntegrationCoursera
  updateIntegration(): void
}

const ModalCoursera: React.FC<modalCourseraProps> = ({
  show,
  onClose,
  integration,
  updateIntegration
}) => {
  const { t } = useTranslation()
  const [checkConnection, setCheckConnection] = useState<boolean | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    getValues
  } = useForm<FormData>()

  useEffect(() => {
    if (integration) {
      const { config } = { ...integration }
      setValue('orgId', config?.orgId || '')
      setValue('appKey', config?.appKey || '')
      setValue('appSecret', config?.appSecret || '')
    }
  }, [integration, setValue])

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      await upsertIntegration({
        integrationType: IntegrationTypes.coursera,
        active: true,
        config: {
          orgId: data.orgId,
          appKey: data.appKey,
          appSecret: data.appSecret,
        },
      })
      updateIntegration()
      handleOnClose()
      SuccessToast(t('configuration.integrations.couseraModal.toastMessages.success'))
    } catch {
      ErrorToast(t('configuration.integrations.couseraModal.toastMessages.error'))
    }
  }

  const testAuthentication = async () => {
    setLoading(true)
    try {
      const data = getValues()
      const response = await testAuth({
        integrationType: IntegrationTypes.coursera,
        active: true,
        config: {
          orgId: data.orgId,
          appKey: data.appKey,
          appSecret: data.appSecret,
        }
      })
      if (response.status === 200) {
        setCheckConnection(true)
      }
      setLoading(false)
    } catch {
      setCheckConnection(false)
      setLoading(false)
    }
  }

  const renderMessageCoursera = () => {
    if (checkConnection === true) {
      return (
        <div className={styles.courseraMessagesSuccess}>
          <FontAwesomeIcon icon={faCheckCircle} color='green' className='mr-2' />
          {t('configuration.integrations.couseraModal.succesCheckCoursera')}
        </div>
      )
    }
    if (checkConnection === false) {
      return (
        <div className={styles.courseraMessagesError}>
          <FontAwesomeIcon icon={faTimesCircle} color='red' className='mr-2' />
          {t('configuration.integrations.couseraModal.errorCheckCoursera')}
        </div>
      )
    }
    return null;
  }

  const handleOnClose = () => {
    onClose()
  }

  const chechErrors = () => {
    return (
      errors.orgId?.type === 'required' ||
      errors.appKey?.type === 'required' ||
      errors.appSecret?.type === 'required'
    )
  }

  return (
    <Modal
      show={show}
      onHide={handleOnClose}
      size="lg"
      className={styles.modalContainer}
    >
      <Modal.Body className={'pt-0 px-0'}>
        <Row className="position-relative">
          <h1>{t('configuration.integrations.couseraModal.title')}</h1>
          <a className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </a>
        </Row>
        <Row>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputLabel
              label={'Organization ID*'}
              className={`pt-3 ${styles.label}`}
            />
            <input
              className={`form-control ${styles.inputBorder}`}
              {...register('orgId', {
                required: true,
              })}
            />
            <InputLabel
              label={'App Key*'}
              className={`pt-3 ${styles.label}`}
            />
            <input
              className={`form-control ${styles.inputBorder}`}
              {...register('appKey', {
                required: true,
              })}
            />
            <InputLabel
              label={'App Secret*'}
              className={`pt-3 ${styles.label}`}
            />
            <input
              className={`form-control ${styles.inputBorder}`}
              {...register('appSecret', {
                required: true,
              })}
              type="password"
            />
            <div>
              <BButton
                className={`${styles.customButton} text-primary`}
                variant="link"
                onClick={() => testAuthentication()}
              >
                <FontAwesomeIcon icon={faArrowsRotate} className={`mr-2 ${loading && styles.spinner}`} />
                {t('configuration.integrations.zoomModal.checkConnection')}
              </BButton>
            </div>
            <div>
              {renderMessageCoursera()}
            </div>
            <ButtonToolbar className="float-right pt-0">
              <Button
                variant={ButtonVariant.Secondary}
                size={ButtonSizes.Small}
                onClick={() => handleOnClose()}
              >
                {t('configuration.integrations.couseraModal.cancel')}
              </Button>
              <Button
                type={ButtonType.Submit}
                variant={ButtonVariant.Primary}
                size={ButtonSizes.Small}
                disabled={chechErrors()}
              >
                {t('configuration.integrations.couseraModal.save')}
              </Button>
            </ButtonToolbar>
          </form>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default ModalCoursera
